import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';

interface LoadingOverlayProps {
  message?: string;
  progress?: number;
  total?: number;
  isPercentage?: boolean;
}

export default function LoadingOverlay({ message = 'Cargando...', progress, total, isPercentage }: LoadingOverlayProps) {
  const [logoUrl, setLogoUrl] = useState<string>('');

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      const { data, error } = await supabase
        .from('site_sections')
        .select('content_json')
        .eq('section_key', 'hero')
        .maybeSingle();

      if (error) {
        console.error('Error loading logo:', error);
        return;
      }

      if (data?.content_json?.logo) {
        setLogoUrl(data.content_json.logo);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  };

  const percentage = progress && total ? Math.round((progress / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Fondo con opacidad suave */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>

      {/* Contenedor centrado del logo y animaciones */}
      <div className="relative flex items-center justify-center">
        {/* Partículas doradas flotantes - centradas con el logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          {[...Array(12)].map((_, i) => {
            const angle = (i * 360) / 12;
            const delay = i * 0.1;
            return (
              <div
                key={i}
                className="absolute w-3 h-3 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full shadow-lg"
                style={{
                  animation: `float-particle-${i} 3s ease-in-out infinite`,
                  animationDelay: `${delay}s`,
                  filter: 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))',
                }}
              >
                <style>{`
                  @keyframes float-particle-${i} {
                    0%, 100% {
                      transform: rotate(${angle}deg) translateX(120px) scale(1);
                      opacity: 0.8;
                    }
                    50% {
                      transform: rotate(${angle + 180}deg) translateX(140px) scale(1.2);
                      opacity: 1;
                    }
                  }
                `}</style>
              </div>
            );
          })}
        </div>

        {/* Anillos dorados giratorios - centrados con el logo */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Anillo exterior */}
          <div 
            className="absolute w-64 h-64 border-4 border-transparent border-t-yellow-400 border-r-amber-500 rounded-full animate-spin"
            style={{ 
              animationDuration: '3s',
              filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.4))'
            }}
          ></div>
          
          {/* Anillo interior */}
          <div 
            className="absolute w-48 h-48 border-4 border-transparent border-b-yellow-400 border-l-amber-500 rounded-full"
            style={{ 
              animation: 'spin 2s linear infinite reverse',
              filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.4))'
            }}
          ></div>
        </div>

        {/* Logo central - perfectamente centrado */}
        <div className="relative z-10 flex items-center justify-center w-48 h-48">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Logo"
              className="w-full h-full object-contain drop-shadow-2xl"
              style={{
                filter: 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.3))',
              }}
            />
          ) : (
            <div className="text-8xl animate-pulse">📸</div>
          )}
        </div>

        {/* Mensaje de carga - debajo del logo */}
        <div className="absolute top-full mt-8 text-center max-w-[90vw] w-96">
          <p className="text-lg font-semibold text-gray-800 mb-2">{message}</p>
          
          {/* Barra de progreso */}
          {progress !== undefined && total !== undefined && (
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                {isPercentage ? (
                  <span className="font-medium">{percentage}%</span>
                ) : (
                  <>
                    <span className="font-medium">{progress} / {total}</span>
                    <span className="text-gray-500">({percentage}%)</span>
                  </>
                )}
              </div>
              
              <div className="max-w-[80vw] w-80 h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner mx-auto">
                <div
                  className="h-full bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-400 rounded-full transition-all duration-300 ease-out relative"
                  style={{ 
                    width: `${percentage}%`,
                    boxShadow: '0 0 20px rgba(251, 191, 36, 0.6)'
                  }}
                >
                  {/* Efecto de brillo */}
                  <div 
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                    style={{
                      animation: 'shimmer 2s infinite',
                    }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}
